<template>
  <PaginaAdministrar
    titulo="Conciliação Bancária"
    :ocultarBotaoAdicionar="true"
    :ocultarFiltroBusca="true"
  >
    <!-- Topo: Seletor de Conta + Ações -->
    <template #header-actions>
      <div class="d-flex align-items-center gap-2 flex-wrap">
        <CampoBusca
          v-model="filtros.idContaBancaria"
          entidade="ContasBancarias"
          campo="descricao"
          :titulo="'Selecione a Conta Bancária'"
          id="idContaBancaria"
          @change="carregarDados"
        />
        <button
          class="btn btn-outline-primary btn-sm"
          :disabled="!filtros.idContaBancaria"
          @click="abrirModalUpload"
        >
          <i class="bi-upload"></i> Importar Extrato
        </button>
        <button
          class="btn btn-outline-info btn-sm"
          :disabled="!filtros.idContaBancaria"
          @click="executarAutoMatch"
        >
          <i class="bi-robot"></i> Auto-Match
        </button>
        <button
          class="btn btn-outline-success btn-sm"
          :disabled="matchesSelecionados.length === 0"
          @click="confirmarLote"
        >
          <i class="bi-check2-all"></i> Confirmar Lote ({{ matchesSelecionados.length }})
        </button>
        <button
          v-if="authStore.pode('FINANCEIRO_CONCILIACAO_CONFIGURACAO_COMPLETO')"
          class="btn btn-outline-secondary btn-sm"
          @click="abrirConfiguracao"
        >
          <i class="bi-gear"></i> Configuração
        </button>
      </div>
    </template>

    <!-- Split-Screen: Extrato (esquerda) × ERP (direita) -->
    <div class="row g-0" style="height: calc(100vh - 250px);">
      <!-- Lado Esquerdo — Extrato do Banco -->
      <div class="col-6 border-end">
        <div class="p-2 bg-body-secondary text-body-secondary small fw-bold">
          Extrato Bancário
          <span class="badge bg-secondary ms-2">{{ lancamentosPaginacao.totalCount || 0 }} lançamentos</span>
        </div>
        <AdminTable
          :colunas="colunasExtrato"
          :itens="lancamentos"
          :paginacao="lancamentosPaginacao"
          :selecao="selecaoExtrato"
          :ordenar="true"
          @page-change="(p) => carregarLancamentos(p)"
          @sort-change="(s) => { filtrosExtrato.sortField = s.campo; filtrosExtrato.sortOrder = s.ordem; carregarLancamentos(); }"
        >
          <template #cell-status="{ item }">
            <span :class="obterClasseStatusGhost(item.lstStatusConciliacao)">
              {{ listas['FIN_CONCIL_ST']?.find(l => l.id === item.lstStatusConciliacao)?.texto || item.lstStatusConciliacao }}
            </span>
          </template>
          <template #cell-acoes="{ item }">
            <button class="btn btn-link btn-sm p-0" title="Detalhes" @click="abrirDetalhe(item)">
              <i class="bi-eye"></i>
            </button>
            <button
              v-if="item.lstStatusConciliacao === 'FIN_CONCIL_ST4'"
              class="btn btn-link btn-sm p-0 text-warning"
              title="Resolver Divergência"
              @click="abrirDivergencia(item)"
            >
              <i class="bi-exclamation-triangle"></i>
            </button>
            <button
              v-if="item.lstStatusConciliacao === 'FIN_CONCIL_ST1'"
              class="btn btn-link btn-sm p-0 text-danger"
              title="Marcar como Órfão"
              @click="marcarOrfao(item)"
            >
              <i class="bi-person-x"></i>
            </button>
            <button
              v-if="item.lstStatusConciliacao === 'FIN_CONCIL_ST1'"
              class="btn btn-link btn-sm p-0 text-muted"
              title="Ignorar"
              @click="ignorarLancamento(item)"
            >
              <i class="bi-x-circle"></i>
            </button>
          </template>
        </AdminTable>
      </div>

      <!-- Lado Direito — Movimentações do ERP -->
      <div class="col-6">
        <div class="p-2 bg-body-secondary text-body-secondary small fw-bold">
          Movimentações do ERP (Baixadas)
          <span class="badge bg-secondary ms-2">{{ movimentacoesPaginacao.totalCount || 0 }} movimentações</span>
        </div>
        <AdminTable
          :colunas="colunasMovimentacoes"
          :itens="movimentacoes"
          :paginacao="movimentacoesPaginacao"
          :selecao="selecaoMovimentacao"
          :ordenar="true"
          @page-change="(p) => carregarMovimentacoes(p)"
          @sort-change="(s) => { filtrosMov.sortField = s.campo; filtrosMov.sortOrder = s.ordem; carregarMovimentacoes(); }"
        />
      </div>
    </div>

    <!-- Footer: Resumo -->
    <template #footer>
      <div class="d-flex justify-content-between align-items-center small text-body-secondary px-2">
        <span>
          <strong>{{ lancamentosConciliados }}</strong> de <strong>{{ lancamentosPaginacao.totalCount || 0 }}</strong> conciliados
        </span>
        <span v-if="ultimoResumo">
          Última importação: {{ ultimoResumo.totalLancamentos }} lançamentos,
          {{ ultimoResumo.matchesAutomaticos }} matches automáticos
        </span>
      </div>
    </template>
  </PaginaAdministrar>

  <!-- ================================================================ -->
  <!-- MODAIS INLINE -->
  <!-- ================================================================ -->

  <!-- Modal de Upload de Extrato -->
  <ModalFormulario
    v-model="showModalUpload"
    titulo="Importar Extrato Bancário"
    tamanho="sm"
    @confirmar="uploadExtrato"
  >
    <form>
      <div class="mb-3">
        <label class="form-label">Arquivo (OFX / CNAB)</label>
        <input type="file" class="form-control" id="arquivo" accept=".ofx,.qfx,.txt" @change="onFileChange" />
      </div>
    </form>
  </ModalFormulario>

  <!-- Modal de Divergência -->
  <ModalFormulario
    v-model="showModalDivergencia"
    titulo="Resolver Divergência"
    tamanho="sm"
    @confirmar="resolverDivergencia"
  >
    <form>
      <div class="mb-3">
        <label class="form-label">Valor Real (Extrato)</label>
        <CampoInput v-model="divergencia.valorReal" type="moeda" id="valorReal" />
      </div>
      <div class="mb-3">
        <label class="form-label">Justificativa</label>
        <textarea v-model="divergencia.justificativa" class="form-control" rows="3" id="justificativa"></textarea>
      </div>
    </form>
  </ModalFormulario>

  <!-- Modal de Detalhe do Lançamento -->
  <ModalFormulario
    v-model="showModalDetalhe"
    :titulo="'Lançamento #' + (lancamentoDetalhe?.idExtratoBancarioLancamento || '')"
    tamanho="lg"
    :ocultarConfirmar="true"
  >
    <div class="row mb-3" v-if="lancamentoDetalhe">
      <div class="col-md-6">
        <label class="form-label fw-bold">Data</label>
        <p class="form-control-plaintext">{{ formatarDataBR(lancamentoDetalhe.dataLancamento) }}</p>
      </div>
      <div class="col-md-6">
        <label class="form-label fw-bold">Valor</label>
        <p class="form-control-plaintext">{{ formatarMoeda(lancamentoDetalhe.valor) }}</p>
      </div>
      <div class="col-12">
        <label class="form-label fw-bold">Descrição Original</label>
        <p class="form-control-plaintext">{{ lancamentoDetalhe.descricaoOriginal || '—' }}</p>
      </div>
      <div class="col-md-6">
        <label class="form-label fw-bold">Código Transação</label>
        <p class="form-control-plaintext">{{ lancamentoDetalhe.codigoTransacaoBanco || '—' }}</p>
      </div>
      <div class="col-md-6">
        <label class="form-label fw-bold">Nº Documento</label>
        <p class="form-control-plaintext">{{ lancamentoDetalhe.numeroDocumento || '—' }}</p>
      </div>
      <div class="col-12">
        <label class="form-label fw-bold">Status</label>
        <p class="form-control-plaintext">
          <span :class="obterClasseStatusGhost(lancamentoDetalhe.lstStatusConciliacao)">
            {{ listas['FIN_CONCIL_ST']?.find(l => l.id === lancamentoDetalhe.lstStatusConciliacao)?.texto || lancamentoDetalhe.lstStatusConciliacao }}
          </span>
        </p>
      </div>
    </div>
  </ModalFormulario>

  <!-- Modal de Configuração de Conciliação -->
  <ModalFormulario
    v-model="showModalConfig"
    titulo="Configuração de Conciliação"
    tamanho="md"
    @confirmar="salvarConfiguracao"
  >
    <form>
      <div class="mb-3">
        <label class="form-label">Tolerância de Valor (R$)</label>
        <CampoInput v-model="configuracao.toleranciaValor" type="moeda" id="toleranciaValor" />
      </div>
      <div class="mb-3">
        <label class="form-label">Auto-Match Ativo</label>
        <CampoSelect v-model="configuracao.autoMatchAtivo" :itens="simNao" id="autoMatchAtivo" />
      </div>
      <div class="mb-3">
        <label class="form-label">Criar Mov. Avulsa para Órfãos</label>
        <CampoSelect v-model="configuracao.criarMovAvulsoParaOrfaos" :itens="simNao" id="criarMovAvulsoParaOrfaos" />
      </div>
      <div class="mb-3">
        <label class="form-label">Status</label>
        <CampoSelect v-model="configuracao.lstStatus" :itens="listaStatus" id="lstStatus" />
      </div>
    </form>
  </ModalFormulario>
</template>

<script>
import { ref, computed } from 'vue'
import api from '@/services/api'
import { usePaginacao } from '@/composables/usePaginacao'
import { useMetadataListasStore } from '@/stores/metadataListas'
import { useAuthStore } from '@/stores/auth'
import { useUI } from '@/composables/useUI'
import { formatarDataBR } from '@/utils/useDataBR'
import PaginaAdministrar from '@/components/base/grid/PaginaAdministrar.vue'
import AdminTable from '@/components/base/grid/AdminTable.vue'
import ModalFormulario from '@/components/base/modal/ModalFormulario.vue'
import CampoInput from '@/components/base/campos/CampoInput.vue'
import CampoSelect from '@/components/base/campos/CampoSelect.vue'
import CampoBusca from '@/components/base/campos/CampoBusca.vue'

export default {
  name: 'ConciliacaoBancaria',
  components: {
    PaginaAdministrar,
    AdminTable,
    ModalFormulario,
    CampoInput,
    CampoSelect,
    CampoBusca
  },
  emits: ['show-notification'],
  setup (props, context) {
    const authStore = useAuthStore()
    const metadataStore = useMetadataListasStore()
    const { obterClasseStatusGhost } = useUI()

    const formatarMoeda = (valor) => {
      if (valor == null) return '-'
      return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(valor)
    }

    // Estado
    const filtros = ref({ idContaBancaria: null })
    const lancamentos = ref([])
    const movimentacoes = ref([])
    const selecaoExtrato = ref([])
    const selecaoMovimentacao = ref([])
    const ultimoResumo = ref(null)
    const arquivoUpload = ref(null)

    // Paginação independente para cada grid
    const { paginacao: lancamentosPaginacao, injetarParametros: injetarParamLanc, atualizarPaginacao: atualizarPaginacaoLanc } = usePaginacao(25)
    const { paginacao: movimentacoesPaginacao, injetarParametros: injetarParamMov, atualizarPaginacao: atualizarPaginacaoMov } = usePaginacao(25)

    // Filtros de ordenação
    const filtrosExtrato = ref({ sortField: 'DataLancamento', sortOrder: 'desc' })
    const filtrosMov = ref({ sortField: 'DataMovimento', sortOrder: 'desc' })

    // Modais
    const showModalUpload = ref(false)
    const showModalDivergencia = ref(false)
    const showModalDetalhe = ref(false)
    const showModalConfig = ref(false)
    const lancamentoDetalhe = ref(null)
    const divergencia = ref({ idLancamento: null, idMovimentacao: null, valorReal: null, justificativa: '' })
    const configuracao = ref({ idConciliacaoConfiguracao: null, toleranciaValor: 5, autoMatchAtivo: true, criarMovAvulsoParaOrfaos: false, lstStatus: 'ST1' })

    // Listas
    const listas = ref({})
    const simNao = ref([{ id: true, texto: 'Sim' }, { id: false, texto: 'Não' }])
    const listaStatus = ref([])

    // Colunas
    const colunasExtrato = [
      { campo: 'dataLancamento', titulo: 'Data', tipo: 'data', largura: 100 },
      { campo: 'descricaoOriginal', titulo: 'Descrição', largura: 200 },
      { campo: 'valor', titulo: 'Valor', tipo: 'moeda', largura: 120 },
      { campo: 'status', titulo: 'Status', tipo: 'template', largura: 130 },
      { campo: 'acoes', titulo: 'Ações', tipo: 'template', largura: 100 }
    ]

    const colunasMovimentacoes = [
      { campo: 'dataMovimento', titulo: 'Data', tipo: 'data', largura: 100 },
      { campo: 'nomeContaBancaria', titulo: 'Conta', largura: 150 },
      { campo: 'valorEfetivoMoedaBanco', titulo: 'Valor', tipo: 'moeda', largura: 120 },
      { campo: 'lstMoedaBanco', titulo: 'Moeda', largura: 60 },
      { campo: 'codigoTransacaoBanco', titulo: 'Cód. Transação', largura: 120 }
    ]

    // Computed
    const matchesSelecionados = computed(() => {
      const result = []
      for (let i = 0; i < Math.min(selecaoExtrato.value.length, selecaoMovimentacao.value.length); i++) {
        result.push({
          idLancamento: selecaoExtrato.value[i],
          idMovimentacao: selecaoMovimentacao.value[i]
        })
      }
      return result
    })

    const lancamentosConciliados = computed(() => {
      if (!lancamentos.value || lancamentos.value.length === 0) return 0
      return lancamentos.value.filter(l =>
        l.lstStatusConciliacao === 'FIN_CONCIL_ST3' ||
        l.lstStatusConciliacao === 'FIN_CONCIL_ST2'
      ).length
    })

    // ================================================================
    // Métodos
    // ================================================================

    const emit = context.emit

    const carregarListas = async () => {
      try {
        await metadataStore.carregarListasParaTabelas([
          'FIN_CONCIL_ST',
          'FIN_EXTRATO_ST',
          'FIN_EXTRATO_TP',
          'TP_MOVIMENTO'
        ])
        listas.value = {
          FIN_CONCIL_ST: metadataStore.obterLista('FIN_CONCIL_ST') || [],
          FIN_EXTRATO_ST: metadataStore.obterLista('FIN_EXTRATO_ST') || [],
          FIN_EXTRATO_TP: metadataStore.obterLista('FIN_EXTRATO_TP') || [],
          TP_MOVIMENTO: metadataStore.obterLista('TP_MOVIMENTO') || []
        }
        listaStatus.value = metadataStore.obterLista('ST') || []
      } catch (error) {
        console.warn('Erro ao carregar listas:', error)
      }
    }

    const carregarLancamentos = async (pageNumber = 1) => {
      if (!filtros.value.idContaBancaria) return
      try {
        const params = injetarParamLanc(pageNumber, new URLSearchParams())
        params.append('idContaBancaria', filtros.value.idContaBancaria)
        if (filtrosExtrato.value.sortField) {
          params.append('sortField', filtrosExtrato.value.sortField)
          params.append('sortOrder', filtrosExtrato.value.sortOrder)
        }
        const response = await api.get('/conciliacao/lancamentos?' + params)
        lancamentos.value = response.data
        atualizarPaginacaoLanc(response.headers)
      } catch (error) {
        const msg = error.response?.data?.message || 'Erro ao carregar lançamentos.'
        emit('show-notification', { message: msg, type: 'error' })
      }
    }

    const carregarMovimentacoes = async (pageNumber = 1) => {
      if (!filtros.value.idContaBancaria) return
      try {
        const params = injetarParamMov(pageNumber, new URLSearchParams())
        params.append('idContaBancaria', filtros.value.idContaBancaria)
        if (filtrosMov.value.sortField) {
          params.append('sortField', filtrosMov.value.sortField)
          params.append('sortOrder', filtrosMov.value.sortOrder)
        }
        const response = await api.get('/conciliacao/movimentacoes?' + params)
        movimentacoes.value = response.data
        atualizarPaginacaoMov(response.headers)
      } catch (error) {
        const msg = error.response?.data?.message || 'Erro ao carregar movimentações.'
        emit('show-notification', { message: msg, type: 'error' })
      }
    }

    const carregarDados = () => {
      carregarLancamentos()
      carregarMovimentacoes()
    }

    // Upload
    const onFileChange = (event) => {
      arquivoUpload.value = event.target.files[0]
    }

    const abrirModalUpload = () => {
      arquivoUpload.value = null
      showModalUpload.value = true
    }

    const uploadExtrato = async () => {
      if (!arquivoUpload.value) {
        emit('show-notification', { message: 'Selecione um arquivo.', type: 'warning' })
        return
      }
      try {
        const formData = new FormData()
        formData.append('arquivo', arquivoUpload.value)
        formData.append('idContaBancaria', filtros.value.idContaBancaria)
        const response = await api.post('/conciliacao/upload', formData)
        ultimoResumo.value = response.data
        emit('show-notification', {
          message: `Extrato importado: ${response.data.totalLancamentos} lançamentos, ${response.data.matchesAutomaticos} matches automáticos.`,
          type: 'success'
        })
        showModalUpload.value = false
        carregarDados()
      } catch (error) {
        const msg = error.response?.data?.message || 'Erro ao importar extrato.'
        emit('show-notification', { message: msg, type: 'error' })
      }
    }

    // Auto-Match
    const executarAutoMatch = async () => {
      try {
        const response = await api.post('/conciliacao/auto-match', {
          idContaBancaria: filtros.value.idContaBancaria
        })
        emit('show-notification', {
          message: `Auto-match executado: ${response.data.length} sugestões geradas.`,
          type: 'success'
        })
        carregarDados()
      } catch (error) {
        const msg = error.response?.data?.message || 'Erro ao executar auto-match.'
        emit('show-notification', { message: msg, type: 'error' })
      }
    }

    // Confirmar Lote
    const confirmarLote = async () => {
      try {
        const response = await api.post('/conciliacao/confirmar-lote', {
          matches: matchesSelecionados.value
        })
        emit('show-notification', {
          message: `${response.data.confirmados} matches confirmados.`,
          type: 'success'
        })
        selecaoExtrato.value = []
        selecaoMovimentacao.value = []
        carregarDados()
      } catch (error) {
        const msg = error.response?.data?.message || 'Erro ao confirmar lote.'
        emit('show-notification', { message: msg, type: 'error' })
      }
    }

    // Divergência
    const abrirDivergencia = (item) => {
      divergencia.value = {
        idLancamento: item.idExtratoBancarioLancamento,
        idMovimentacao: item.idMovimentacaoBancaria,
        valorReal: item.valor,
        justificativa: ''
      }
      showModalDivergencia.value = true
    }

    const resolverDivergencia = async () => {
      try {
        await api.post('/conciliacao/divergencia', divergencia.value)
        emit('show-notification', { message: 'Divergência resolvida.', type: 'success' })
        showModalDivergencia.value = false
        carregarDados()
      } catch (error) {
        const msg = error.response?.data?.message || 'Erro ao resolver divergência.'
        emit('show-notification', { message: msg, type: 'error' })
      }
    }

    // Órfão
    const marcarOrfao = async (item) => {
      try {
        const response = await api.post('/conciliacao/orfao', {
          idLancamento: item.idExtratoBancarioLancamento
        })
        const msg = response.data.movimentacaoCriada
          ? 'Lançamento marcado como órfão e movimentação avulsa criada.'
          : 'Lançamento marcado como órfão.'
        emit('show-notification', { message: msg, type: 'success' })
        carregarDados()
      } catch (error) {
        const msg = error.response?.data?.message || 'Erro ao marcar como órfão.'
        emit('show-notification', { message: msg, type: 'error' })
      }
    }

    // Ignorar
    const ignorarLancamento = async (item) => {
      try {
        await api.post('/conciliacao/ignorar', {
          idLancamento: item.idExtratoBancarioLancamento
        })
        emit('show-notification', { message: 'Lançamento ignorado.', type: 'success' })
        carregarDados()
      } catch (error) {
        const msg = error.response?.data?.message || 'Erro ao ignorar lançamento.'
        emit('show-notification', { message: msg, type: 'error' })
      }
    }

    // Detalhe
    const abrirDetalhe = (item) => {
      lancamentoDetalhe.value = item
      showModalDetalhe.value = true
    }

    // Configuração
    const abrirConfiguracao = async () => {
      try {
        const response = await api.get('/conciliacao/configuracao')
        const c = response.data
        configuracao.value = {
          idConciliacaoConfiguracao: c.idConciliacaoConfiguracao,
          toleranciaValor: c.toleranciaValor,
          autoMatchAtivo: c.autoMatchAtivo,
          criarMovAvulsoParaOrfaos: c.criarMovAvulsoParaOrfaos,
          lstStatus: c.lstStatus
        }
        showModalConfig.value = true
      } catch (error) {
        const msg = error.response?.data?.message || 'Erro ao carregar configuração.'
        emit('show-notification', { message: msg, type: 'error' })
      }
    }

    const salvarConfiguracao = async () => {
      try {
        await api.put(`/conciliacao/configuracao/${configuracao.value.idConciliacaoConfiguracao}`, configuracao.value)
        emit('show-notification', { message: 'Configuração salva.', type: 'success' })
        showModalConfig.value = false
      } catch (error) {
        const msg = error.response?.data?.message || 'Erro ao salvar configuração.'
        emit('show-notification', { message: msg, type: 'error' })
      }
    }

    // Init
    carregarListas()

    return {
      authStore,
      filtros,
      lancamentos,
      movimentacoes,
      lancamentosPaginacao,
      movimentacoesPaginacao,
      selecaoExtrato,
      selecaoMovimentacao,
      matchesSelecionados,
      lancamentosConciliados,
      ultimoResumo,
      showModalUpload,
      showModalDivergencia,
      showModalDetalhe,
      showModalConfig,
      lancamentoDetalhe,
      divergencia,
      configuracao,
      listas,
      simNao,
      listaStatus,
      colunasExtrato,
      colunasMovimentacoes,
      filtrosExtrato,
      filtrosMov,
      onFileChange,
      abrirModalUpload,
      uploadExtrato,
      executarAutoMatch,
      confirmarLote,
      abrirDivergencia,
      resolverDivergencia,
      marcarOrfao,
      ignorarLancamento,
      abrirDetalhe,
      abrirConfiguracao,
      salvarConfiguracao,
      carregarDados,
      carregarLancamentos,
      carregarMovimentacoes,
      obterClasseStatusGhost,
      formatarDataBR,
      formatarMoeda
    }
  }
}
</script>
